/**
 * AnnouncementBanner ✦ iter222h — slim gold strip mounted above
 * the global nav on the commercial-marketing surfaces ONLY.
 *
 * Strict rules (per Craig's iter222h brief):
 *  • Full width. Height 36–44px desktop, 32–38px mobile.
 *  • Gold gradient background, dark walnut text.
 *  • Whole banner is a single anchor → /photobooth-hire.
 *  • Rotates four messages every 5 seconds with a subtle fade.
 *  • No marquee, no aggressive motion.
 *  • Honours `prefers-reduced-motion` (no fade, no rotation).
 *  • NEVER renders on core-platform surfaces (`/event/`, `/access/`,
 *    voice/preserve/print/recap/one-word flows). Atmosphere
 *    contract stays intact.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Four rotating messages — order is intentional: coverage area
// first (visitor-orienting), then the headline £350 hook, then
// service breadth, then the action prompt.
const MESSAGES = [
  '✦ Photobooth Hire Available Across North Wales, Cheshire, Liverpool & Manchester',
  '✦ Our Most Popular Package — Unlimited Prints £350',
  '✦ Infinity Mirror Booth · Selfie Pod · Audio Guestbook',
  '✦ Check Availability For Your Event Today',
];

const ROTATION_MS = 5000;
const FADE_MS = 380;

// Allow-list of route prefixes where the banner is welcome. Anything
// not matching here renders nothing, so the banner cannot leak onto
// the core platform's atmosphere-protected surfaces.
const ALLOWED_PREFIXES = [
  '/photobooth-hire',
  '/photobooth-hire-cheshire',
  '/photobooth-hire-north-wales',
  '/photobooth-hire-liverpool',
  '/photobooth-hire-manchester',
  '/photobooth-hire-wirral',
  '/photobooth-hire-shropshire',
  '/photobooth-hire-lancashire',
  '/photobooth-hire-merseyside',
  '/venues',
  '/events',
  '/schools',
  '/about',
  '/pricing',
  '/contact',
];

const isMarketingSurface = (pathname) => {
  if (pathname === '/' || pathname === '') return true;
  return ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
};

const AnnouncementBanner = () => {
  const location = useLocation();
  const visible = useMemo(() => isMarketingSurface(location.pathname), [location.pathname]);

  const [index, setIndex] = useState(0);
  const [fading, setFading] = useState(false);

  // Respect the user's reduced-motion preference — no rotation,
  // no fade, just the first message held still.
  const reduceMotion = typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!visible || reduceMotion) return undefined;
    const id = setInterval(() => {
      setFading(true);
      // After the fade-out completes, swap the message and fade in.
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % MESSAGES.length);
        setFading(false);
      }, FADE_MS);
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, [visible, reduceMotion]);

  if (!visible) return null;

  return (
    <Link
      to="/photobooth-hire"
      data-testid="announcement-banner"
      aria-label="Photobooth hire — view packages"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100%', minHeight: 38,
        padding: '8px clamp(14px, 4vw, 32px)',
        background: 'linear-gradient(90deg, #C9A35A 0%, #E2C77B 50%, #C9A35A 100%)',
        color: '#1A1108',
        fontFamily: 'Georgia, "Cormorant Garamond", "Times New Roman", serif',
        fontStyle: 'italic',
        fontSize: 13,
        letterSpacing: '0.04em',
        textAlign: 'center',
        textDecoration: 'none',
        boxShadow: '0 1px 0 rgba(0,0,0,0.18) inset, 0 -1px 0 rgba(255,255,255,0.32) inset',
        position: 'relative',
        zIndex: 70,
        cursor: 'pointer',
      }}
    >
      <span
        data-testid="announcement-message"
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          opacity: fading ? 0 : 1,
          transition: reduceMotion ? 'none' : `opacity ${FADE_MS}ms ease`,
        }}
      >
        {MESSAGES[index]}{' '}
        <span style={{ marginLeft: 6, fontStyle: 'normal', fontWeight: 600 }}>→</span>
      </span>
    </Link>
  );
};

export default AnnouncementBanner;
